import { CommonModule } from "@angular/common";
import { Component, ViewChild, signal } from '@angular/core';
import { MarkdownModule } from "ngx-markdown";
import {
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  ColDef
} from "ag-grid-community";
import stringify from "json-stringify-pretty-compact";

import { EzBaseComponent } from "@template-ui/components/ez-base.component";
import { EzFormInputType, EzGridColTypes } from "@template-ui/types";
import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { PanelHeaderConfig, InputConfig, FormValuesObject } from "@template-ui/types";
import { EzGridComponent } from "@template-ui";
import { createTrigger } from "@template-ui/helpers";

@DocMeta({
  hide: true,
  showcaseTitle:"AutoSizeStrategy",
  // showcaseDesc: `
  // ## AutoSizeStrategy
  // AutoSizeStrategy is a property of AG-Grid which is accessible through ??.

  // Use the drop-down to change the AutoSizeStrategy to see how it affects the column widths of the grid.
  // `
})
@Component({
    selector: 'ui-grid-sample-auto-size-strategy, ez-grid-sample-auto-size-strategy',
    imports: [CommonModule, MarkdownModule, UiLibraryModule],
    templateUrl: './ez-grid-sample-auto-size-strategy.component.html',
    styleUrl: './ez-grid-sample-auto-size-strategy.component.scss'
})
export class EzGridSampleAutoSizeStrategyComponent extends EzBaseComponent {

 @ViewChild('ezGrid') ezGrid?: EzGridComponent;

  starWarsEndpoint = "mock/StarWarsActors";

  // For assigning to reloadOnChangeVar, could use a signal or just a primitive
  reloadVar = 0;

  reloadTrig = createTrigger();  // Can be pulsed right in the template, no need for reload() method.

  reload() {
    this.reloadVar++;          // Just a chnage in value is needed. Actual value is meaningless.
  }

  docMd = `
### Short Version
Just don't use AutoSizeStrategy. It's only useful if you're not providing columnDefs.

### Strategy Types
- \`fitCellContents\`: Sizes columns based on cell content.
- \`fitGridWidth\`: Distributes column widths to fill the grid width.
- \`fitProvidedWidth\`: Sizes columns to fit a specific pixel width (\`providedWidth\`).
- \`none\`: Disables automatic sizing.

### Optional Properties
- \`skipHeader: boolean\` — Applies only to \`fitCellContents\`. If \`true\`, header text is ignored.
- \`width: number\` — Applies only to \`fitProvidedWidth\`.

### Column-Level Overrides
- \`width\`: Fixed column width (overrides autosizing).
- \`minWidth\`: Minimum width constraint during autosizing.
- \`maxWidth\`: Maximum width constraint during autosizing.
- \`flex\`: Used with \`fitGridWidth\` to proportionally distribute space.

### Behavior Notes
- Autosizing occurs once on grid init.
- Works best with client-side row model.
- Manual resizing still possible via column API.
`

  ezPanelHeaderConfig: PanelHeaderConfig = {
    panelHeaderTitle: 'Star Wars Actors',
    titleClickToReload: true,
    showReloadButton: true
  };

  starWarsActorColDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', maxWidth:80 },
    { field: 'firstName', headerName: 'First Name' },
    { field: 'lastName', headerName: 'Last Name' },
    { field: 'actorId', headerName: 'Actor ID' },
    { field: 'dateOfBirth', headerName: 'Date of Birth', filter: 'agDateColumnFilter' },
    { field: 'notableRole', headerName: 'Notable Role' },
    { field: 'yearsActive', headerName: 'Years Active', filter: 'agNumberColumnFilter' },
    { field: 'awards', headerName: 'Awards' },
    { field: 'imdbUrl', headerName: 'IMDB' },
    { field: 'trilogy', headerName: 'Trilogy' },
    { field: 'isLiving', headerName: 'Living' },
    { field: 'academyAwardNominations', wrapHeaderText: true, filter: 'agNumberColumnFilter' },
    { field: 'biography', headerName: 'Biography', autoHeight: true, minWidth: 300, type: [EzGridColTypes.WrapText] }
  ];

  get starWarsActorColDefsString(): string { return stringify(this.starWarsActorColDefs, {maxLength: 200, indent: 2})}
  set starWarsActorColDefsString(value: string) {
    this.dlog(`set starWarsActorColDefsString`);
    this.starWarsActorColDefs = JSON.parse(value);
  }

  ezAdditionActionFormValues: Record<string, unknown> = {
    blankRows: false
  };

  colDefsPanelHeaderConfig: PanelHeaderConfig = {
    additionalActions: [
      {
        name: "useColDefs",
        type: "checkbox",
        label: "Use Column Definitions"
      } as InputConfig
    ]
  }

  colDefsPanelAddActFormValues: FormValuesObject = {
    useColDefs: false
  }

  autoSizeFormConfig: InputConfig[] = [
    {
      name: "autoSizeStrategy",
      label: "Type",
      type: "select",
      width: "1x",
      options: [
        { value: "none", viewValue: "none" },
        { value: "fitGridWidth", viewValue: "fitGridWidth" },
        { value: "fitProvidedWidth", viewValue: "fitProvidedWidth" },
        { value: "fitCellContents", viewValue: "fitCellContents" },
      ]
    },
    {
      name: "defaultMinWidth",
      type: "number",
      disabled: () => { return this.autoSizeStrategyFormValues['autoSizeStrategy'] != "fitGridWidth"; }
    },
    {
      name: "defaultMaxWidth",
      type: "number",
      disabled: () => { return this.autoSizeStrategyFormValues['autoSizeStrategy'] != "fitGridWidth"; }
    },
    {
      name: "width",
      type: "number",
      disabled: () => { return this.autoSizeStrategyFormValues['autoSizeStrategy'] != "fitProvidedWidth"; }
    },
    {
      name: "skipHeader",
      label: "Skip Header",
      type: "checkbox",
      disabled: () => { return this.autoSizeStrategyFormValues['autoSizeStrategy'] != "fitCellContents"; }
    },
    {
      name: "autoSize",
      type:"button",
      buttonText:"Re-render with new autoSizeStrategy values"
    },

  ];

  colDefForm: InputConfig[] = [
    {
      name: "colDefJson",
      type: "textarea",
      width: "fill",
      textAreaRows: 15,
      disabled: () => { return !this.colDefsPanelAddActFormValues['useColDefs'] as boolean}
    }
  ];

  colDefFormValues: FormValuesObject = {
    colDefJson: this.starWarsActorColDefsString
  }

  onUseColDefChanged(_event?: Partial<FormValuesObject>)
  {
    if(!this.colDefsPanelAddActFormValues['useColDefs'] as boolean)
    {
      requestAnimationFrame(() => {
        //this.ezGrid?.reload();
        //this.reload();
        this.reloadTrig.pulse();
      });
    }
  }

  onColDefsChange(event?: Partial<FormValuesObject>)
  {
    const colDefJson = event?.['colDefJson'];
    if (typeof colDefJson === "string") {
      this.starWarsActorColDefs = JSON.parse(colDefJson) as ColDef[];
    }
    //this.dlog(`Forms value changed to ${this.starWarsActorColDefsString}`)
  }

  gridInstanceId = 1;
  onAutoSizeClick(_event?: unknown)
  {
    this.forceGridRerender();
  }

  /**
   * Force the grid to re-render which will apply
   * the current autoSizeStrategy.
   */
  forceGridRerender()
  {
    this.gridInstanceId = 0;
     setTimeout(() => {
      this.gridInstanceId++;
    }, 10);
  }

  autoSizeStrategyFormValues: FormValuesObject = {
    autoSizeStrategy: "none",
    defaultMinWidth: 100,
    defaultMaxWidth: 300,
    width: 200,
    skipHeader: false
  };

  autoSizeStrategy?: SizeColumnsToFitGridStrategy | SizeColumnsToFitProvidedWidthStrategy | SizeColumnsToContentStrategy;

  colDefsJson()
  {
    return stringify( this.starWarsActorColDefs, {maxLength: 200, indent: 2});
  }

  onAutoSizeStregyTypeChange(event: Partial<FormValuesObject>)
  {
    if(Object.hasOwn(event, 'autoSizeStrategy') || Object.hasOwn(event, 'skipHeader'))
    {
      // Refresh the grid
      this.forceGridRerender();
    }
  }
  onAutoSizeStrategyFormValuesChange(event: Partial<FormValuesObject>)
  {
      switch(event['autoSizeStrategy'])
      {
        case "none":
          this.autoSizeStrategy = undefined;
          break;

        case "fitGridWidth":
          this.autoSizeStrategy = {type: event['autoSizeStrategy'], defaultMinWidth: this.autoSizeStrategyFormValues['defaultMinWidth'] as number, defaultMaxWidth: this.autoSizeStrategyFormValues['defaultMaxWidth'] as number };
          break;

        case "fitProvidedWidth":
          this.autoSizeStrategy = { type: event['autoSizeStrategy'], width: this.autoSizeStrategyFormValues['width'] as number };
          break;

        case "fitCellContents":
          this.autoSizeStrategy = { type: event['autoSizeStrategy'], skipHeader: this.autoSizeStrategyFormValues['skipHeader'] as boolean };
          break;
      }
  }

  constructor() {
    super();
    this.thisClassName = 'EzGridSampleAutoSizeStrategyComponent';

    this.ezLookup.registerLookup("Empire", this.starWarsEndpoint);  // Doesn't matter because the data interceptor gets it before the crud service
  }
}

 // autoSizeStrategy?: SizeColumnsToFitGridStrategy | SizeColumnsToFitProvidedWidthStrategy | SizeColumnsToContentStrategy;

// /** Limit a column width when auto-sizing to fit grid width. Column specific? */
// export interface SizeColumnsToFitGridColumnLimits {
//     colId: string;
//     /** Minimum width for this column (does not override the column minimum width) */
//     minWidth?: number;
//     /** Maximum width for this column (does not override the column maximum width) */
//     maxWidth?: number;
// }
// /** Auto-size columns to fit the grid width. */
// export interface SizeColumnsToFitGridStrategy {
//     type: 'fitGridWidth';
//     /** Default minimum width for every column (does not override the column minimum width). */
//     defaultMinWidth?: number;
//     /** Default maximum width for every column (does not override the column maximum width). */
//     defaultMaxWidth?: number;
//     /** Provide to limit specific column widths when sizing. */
//     columnLimits?: SizeColumnsToFitGridColumnLimits[];
// }
// /** Auto-size columns to fit a provided width. */
// export interface SizeColumnsToFitProvidedWidthStrategy {
//     type: 'fitProvidedWidth';
//     width: number;
// }
// /**
//  * Auto-size columns to fit their cell contents.
//  * Only works for Client-Side Row Model and Server-Side Row Model.
//  */
// export interface SizeColumnsToContentStrategy {
//     type: 'fitCellContents';
//     /** If true, the header won't be included when calculating the column widths. */
//     skipHeader?: boolean;
//     /** If not provided will auto-size all columns. Otherwise will size the specified columns. */
//     colIds?: string[];
// }



